import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Collapse, Divider, Stack, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

export interface ResponsiveCardField<TRow> {
    label: string;
    /** Either a field name on the row or a renderer that receives the row. */
    field?: keyof TRow;
    render?: (row: TRow) => React.ReactNode;
}

interface ResponsiveCardListProps<TRow> {
    rows: TRow[];
    /**
     * A prominent header rendered at the top of each card (e.g. shift date, worker name).
     */
    primary: (row: TRow) => React.ReactNode;
    /**
     * Optional secondary/subtitle element under the primary line (e.g. a status chip).
     */
    secondary?: (row: TRow) => React.ReactNode;
    /**
     * Ordered list of label / value pairs shown as a definition list inside each card.
     */
    fields: ResponsiveCardField<TRow>[];
    /**
     * Optional additional label/value rows that are hidden behind a
     * "Show more" toggle at the bottom of each card.
     */
    expandableFields?: ResponsiveCardField<TRow>[];
    getRowId: (row: TRow) => string | number;
    emptyMessage?: string;
}

const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={1.5}
    >
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: 'uppercase', flexShrink: 0 }}
        >
            {label}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
            {value}
        </Typography>
    </Stack>
);

function resolveValue<TRow>(row: TRow, { field, render }: ResponsiveCardField<TRow>): React.ReactNode {
    if (render) return render(row);
    return String((row as any)[field as keyof TRow] ?? '—');
}

/**
 * Renders a list of cards in place of a data table. Intended for phone/tablet
 * views where a horizontal table becomes unusable.
 */
function ResponsiveCardList<TRow>({
    rows,
    primary,
    secondary,
    fields,
    expandableFields,
    getRowId,
    emptyMessage = 'No records to display',
}: ResponsiveCardListProps<TRow>) {
    const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());

    const toggle = (id: string | number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    if (!rows.length) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                </Typography>
            </Box>
        );
    }

    const hasExpandable = !!expandableFields?.length;

    return (
        <Stack spacing={1.25}>
            {rows.map((row) => {
                const id = getRowId(row);
                const isExpanded = expandedIds.has(id);
                return (
                    <Card key={id} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1.75, '&:last-child': { pb: hasExpandable ? 0.5 : 1.75 } }}>
                            <Stack
                                direction="row"
                                alignItems="flex-start"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ mb: 1 }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                    {primary(row)}
                                </Typography>
                                {secondary?.(row)}
                            </Stack>
                            <Stack spacing={0.75}>
                                {fields.map((f) => (
                                    <FieldRow key={f.label} label={f.label} value={resolveValue(row, f)} />
                                ))}
                            </Stack>

                            {hasExpandable && (
                                <>
                                    <Collapse in={isExpanded} unmountOnExit>
                                        <Divider sx={{ my: 1.25 }} />
                                        <Stack spacing={0.75}>
                                            {expandableFields!.map((f) => (
                                                <FieldRow key={f.label} label={f.label} value={resolveValue(row, f)} />
                                            ))}
                                        </Stack>
                                    </Collapse>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                                        <Button
                                            size="small"
                                            onClick={() => toggle(id)}
                                            endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                                            aria-expanded={isExpanded}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            {isExpanded ? 'Show less' : 'Show more'}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
}

export default ResponsiveCardList;
